/**
 * Cross-field warning rules for the admin-ui `/settings` Phase 2 editor.
 *
 * Lives in `@prospect/shared` (not `@prospect/google` next to
 * `AGENT_CONFIG_METADATA`) because the SPA needs to evaluate them
 * client-side and `@prospect/google` pulls in `googleapis`, which is
 * Node-only. Both packages are platform-wide config UX surfaces — they
 * just sit on opposite sides of the browser/Node split.
 *
 * The posture is **warn-and-allow**: a rule firing surfaces a chip on
 * every affected row but never blocks save. The agent-side Zod
 * schemas at run start are the loud-fail backstop. This lets an
 * operator make a multi-step edit (e.g. raise the curator cap, then
 * raise the validator cap) without fighting the form mid-edit.
 *
 * To add a rule: append a new entry. The `affectedKeys` array drives
 * which rows render the chip, and `check` returns the chip's message
 * (or `null` when the rule does not currently fire for the supplied
 * snapshot). `id` exists so a future rule deduper / per-rule-dismiss
 * UX has a stable handle.
 */

/**
 * The minimal value-snapshot shape a rule receives. `null` means the
 * cell is missing from the live Sheet entirely; `undefined` means the
 * key is not in the snapshot (the editor has the row but no value yet
 * — should be rare in practice).
 */
export type AgentConfigValueSnapshot = Readonly<
  Record<string, string | number | null | undefined>
>;

export interface AgentConfigWarningRule {
  /**
   * Stable identifier for the rule. Used as the React key on the chip
   * and gives a future dismiss-rule UX a handle.
   */
  readonly id: string;
  /**
   * Keys that should render the chip when the rule fires. The editor
   * iterates rules per row and shows every chip whose `affectedKeys`
   * contains the row's key.
   */
  readonly affectedKeys: readonly string[];
  /**
   * Evaluate the rule against the current snapshot. Return the chip
   * message when the rule fires, `null` otherwise. Functions are pure
   * so the editor can re-evaluate cheaply on every keystroke.
   */
  readonly check: (snapshot: AgentConfigValueSnapshot) => string | null;
}

/**
 * Coerce a snapshot cell to a finite number, or `null` if it isn't one.
 * Numeric Sheet cells come through as numbers; string cells the
 * operator typed into a numeric editor come through as strings — both
 * are accepted to match the warn-and-allow posture (the agent boot Zod
 * is what loud-fails on garbage).
 */
function toFiniteNumber(value: string | number | null | undefined): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.length > 0) {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

/**
 * The current rule set. Adding a rule is a one-line append plus a
 * unit-test entry in `agent-config-warnings.test.ts`.
 */
export const AGENT_CONFIG_WARNING_RULES: readonly AgentConfigWarningRule[] = [
  {
    id: "validator_max_rows_lt_max_drafts",
    affectedKeys: ["validator_max_rows_per_run", "max_new_drafts_per_run"],
    check: (snapshot) => {
      const validatorCap = toFiniteNumber(snapshot.validator_max_rows_per_run);
      const draftCap = toFiniteNumber(snapshot.max_new_drafts_per_run);
      if (validatorCap === null || draftCap === null) return null;
      if (validatorCap >= draftCap) return null;
      return `validator_max_rows_per_run (${validatorCap}) < max_new_drafts_per_run (${draftCap}) — the validator would skip rows the curator wrote in the same run.`;
    },
  },
];

/**
 * Evaluate every rule against the snapshot and return the firing
 * messages keyed by rule id. The editor uses this to render chips on
 * rows whose key appears in any firing rule's `affectedKeys`.
 */
export function evaluateAgentConfigWarnings(
  snapshot: AgentConfigValueSnapshot,
): Map<string, string> {
  const fired = new Map<string, string>();
  for (const rule of AGENT_CONFIG_WARNING_RULES) {
    const message = rule.check(snapshot);
    if (message !== null) fired.set(rule.id, message);
  }
  return fired;
}

/**
 * One firing rule's projection onto a row — the rule's id alongside
 * its message. Returning the id lets the renderer use it as a stable
 * React key (cheaper to maintain than an array-index key) and gives a
 * future per-rule dismiss UX a handle.
 */
export interface AgentConfigKeyWarning {
  readonly ruleId: string;
  readonly message: string;
}

/**
 * Project the `evaluateAgentConfigWarnings` result onto a single key —
 * the messages whose rules touch that key. Used by the per-row chip
 * renderer.
 */
export function warningsForKey(
  fired: ReadonlyMap<string, string>,
  key: string,
): AgentConfigKeyWarning[] {
  const out: AgentConfigKeyWarning[] = [];
  for (const rule of AGENT_CONFIG_WARNING_RULES) {
    if (!rule.affectedKeys.includes(key)) continue;
    const message = fired.get(rule.id);
    if (message !== undefined) out.push({ ruleId: rule.id, message });
  }
  return out;
}
